// Cross-links (N4): the item panel's "Received": every delivery of this item, newest first (when, how many good and
// rejected, which PO and vendor, where it went), each opening its PO's Receipts tab. The usual table (it scrolls inside
// past 12), its View and Columns in the row under the title.
import { useMemo, useState } from "react";
import type { Po, Receipt, ReceiptLine } from "../../../../shared/db.gen.ts";
import { REJECT_REASON_LABELS } from "../../../../shared/receiving.ts";
import { dayText, qty } from "../../lib/format.ts";
import { useRows } from "../../lib/sync.ts";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { Section } from "../../ui/RecordPanel.tsx";
import { PaneLink, usePaneOpen } from "../../ui/SidePane.tsx";
import { poPath, vendorLabel } from "../po/lib.ts";

type Row = { id: string; line: ReceiptLine; receipt: Receipt; po: Po; vendor: string; location: string };

export function ItemReceipts({ itemId }: { itemId: string }) {
  const lines = useRows("receipt_line");
  const receipts = useRows("receipt");
  const pos = useRows("po");
  const vendors = useRows("vendor");
  const locations = useRows("location");
  const openPane = usePaneOpen();
  const rows = useMemo<Row[]>(() => {
    const receiptById = new Map(receipts.map((r) => [r.id, r]));
    const poById = new Map(pos.map((p) => [p.id, p]));
    return lines
      .filter((l) => l.item_id === itemId)
      .flatMap((l) => {
        const receipt = receiptById.get(l.receipt_id);
        const po = receipt && poById.get(receipt.po_id);
        return receipt && po
          ? [
              {
                id: l.id,
                line: l,
                receipt,
                po,
                vendor: vendorLabel(vendors.find((v) => v.id === po.vendor_id)),
                location: locations.find((x) => x.id === receipt.location_id)?.name ?? "",
              },
            ]
          : [];
      });
  }, [lines, receipts, pos, vendors, locations, itemId]);
  if (!rows.length) return null;
  const columns: Column<Row>[] = [
    {
      key: "received_at",
      header: "Date",
      width: "80px",
      value: (r) => r.receipt.received_at,
      text: (r) => dayText(r.receipt.received_at),
      cell: (r) => <span className="text-[12px] text-muted">{dayText(r.receipt.received_at)}</span>,
    },
    {
      key: "po",
      header: "PO",
      width: "90px",
      value: (r) => r.po.number,
      // Ctrl/⌘-click: the PO's own page, on its Receipts tab
      cell: (r) => (
        <PaneLink kind="po" id={r.po.id} to={`${poPath(r.po.id)}&tab=receipts`} className="font-mono text-[12px] font-medium hover:underline">
          {r.po.number}
        </PaneLink>
      ),
    },
    { key: "vendor", header: "Vendor", width: "minmax(110px,1fr)", cell: (r) => <span className="truncate">{r.vendor}</span> },
    { key: "location", header: "To", width: "minmax(90px,1fr)", cell: (r) => <span className="truncate text-faint">{r.location}</span> },
    {
      key: "rejected",
      header: "Rejected",
      width: "84px",
      align: "right",
      value: (r) => Number(r.line.qty_rejected),
      cell: (r) =>
        Number(r.line.qty_rejected) > 0 && (
          <span className="text-[12px] text-danger" title={REJECT_REASON_LABELS[r.line.reject_reason as keyof typeof REJECT_REASON_LABELS]}>
            {qty(r.line.qty_rejected)} rejected
          </span>
        ),
    },
    {
      key: "good",
      header: "Good",
      width: "72px",
      align: "right",
      value: (r) => Number(r.line.qty_received),
      cell: (r) => <span className="font-mono text-[12px]">{qty(r.line.qty_received)}</span>,
      total: (rs) => <span className="font-mono">{qty(rs.reduce((n, r) => n + Number(r.line.qty_received), 0))}</span>,
    },
    {
      key: "receipt",
      header: "Receipt",
      width: "100px",
      optional: true,
      value: (r) => r.receipt.number,
      cell: (r) => <span className="font-mono text-[12px]">{r.receipt.number}</span>,
    },
    {
      key: "notes",
      header: "Notes",
      width: "minmax(120px,1fr)",
      optional: true,
      value: (r) => r.line.notes,
      cell: (r) => (
        <span className="truncate text-faint" title={r.line.notes}>
          {r.line.notes}
        </span>
      ),
    },
  ];
  return (
    <Section title="Received" count={rows.length}>
      <div className="px-2 pb-2">
        <DataTable
          fit
          rows={rows}
          columns={columns}
          defaultSort={{ key: "received_at", desc: true }}
          onOpen={(r) => openPane("po", r.po.id)}
          exportName="Item received"
          pageFilters={false}
          rowNoun={["delivery", "deliveries"]}
        />
      </div>
    </Section>
  );
}
